import type { ReactElement } from 'react';
import { Grid3x3, List, Star, Box, Sparkles, ImageIcon, Sparkle } from 'lucide-react';

import type { AvatarItem } from '@/services/avatarApi';
import { deriveAvatarFlags } from '@/components/avatars/AvatarBadges';
import { cn } from '@/lib/cn';

export type AvatarFilter = 'all' | '3d' | 'animated' | 'featured' | 'photo';
export type AvatarView = 'grid' | 'list';

interface AvatarFiltersProps {
  items: AvatarItem[];
  activeFilter: AvatarFilter;
  view: AvatarView;
  onFilterChange: (f: AvatarFilter) => void;
  onViewChange: (v: AvatarView) => void;
}

/**
 * Counts per filter dimension. Derived once at the parent (Avatars.tsx) and
 * passed to the chips so they can render `(N)` next to the label and skip
 * filters that would yield an empty list.
 */
export interface FilterCounts {
  total: number;
  threeD: number;
  animated: number;
  featured: number;
  photo: number;
}

export function computeFilterCounts(items: AvatarItem[]): FilterCounts {
  let threeD = 0;
  let animated = 0;
  let featured = 0;
  let photo = 0;
  for (const a of items) {
    const f = deriveAvatarFlags(a);
    if (f.has3d || f.inFlightMesh) threeD++;
    if (f.hasAnimation) animated++;
    if (f.isFeatured) featured++;
    if (f.isPhotoBase) photo++;
  }
  return { total: items.length, threeD, animated, featured, photo };
}

export function applyAvatarFilter(items: AvatarItem[], filter: AvatarFilter): AvatarItem[] {
  if (filter === 'all') return items;
  return items.filter((a) => {
    const f = deriveAvatarFlags(a);
    switch (filter) {
      case '3d':
        return f.has3d || f.inFlightMesh;
      case 'animated':
        return f.hasAnimation;
      case 'featured':
        return f.isFeatured;
      case 'photo':
        return f.isPhotoBase;
      default:
        return true;
    }
  });
}

const CHIPS: Array<{ key: AvatarFilter; label: string; icon: ReactElement; countKey: keyof FilterCounts }> = [
  { key: 'all',      label: 'All',      icon: <Sparkle aria-hidden className="h-3.5 w-3.5" />,     countKey: 'total' },
  { key: '3d',       label: '3D',       icon: <Box aria-hidden className="h-3.5 w-3.5" />,         countKey: 'threeD' },
  { key: 'animated', label: 'Animated', icon: <Sparkles aria-hidden className="h-3.5 w-3.5" />,    countKey: 'animated' },
  { key: 'featured', label: 'Hub',      icon: <Star aria-hidden className="h-3.5 w-3.5" />,        countKey: 'featured' },
  { key: 'photo',    label: 'Photo',    icon: <ImageIcon aria-hidden className="h-3.5 w-3.5" />,   countKey: 'photo' },
];

export function AvatarFilters({
  items,
  activeFilter,
  view,
  onFilterChange,
  onViewChange,
}: AvatarFiltersProps) {
  const counts = computeFilterCounts(items);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-fg-muted">
          <span className="font-bold text-fg">{counts.total}</span> avatars
          {counts.threeD > 0 && (
            <>
              {' · '}
              <span className="font-bold text-fg">{counts.threeD}</span> 3D
            </>
          )}
          {counts.animated > 0 && (
            <>
              {' · '}
              <span className="font-bold text-fg">{counts.animated}</span> animated
            </>
          )}
          {counts.featured > 0 && (
            <>
              {' · '}
              <span className="font-bold text-fg">{counts.featured}</span> Hub
            </>
          )}
        </p>
        <div role="group" aria-label="View mode" className="flex items-center gap-0.5 rounded-full bg-bg-secondary p-0.5">
          {(['grid', 'list'] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => onViewChange(v)}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                view === v ? 'bg-brand text-brand-fg' : 'text-fg-muted',
              )}
            >
              {v === 'grid' ? (
                <Grid3x3 aria-hidden className="h-3.5 w-3.5" />
              ) : (
                <List aria-hidden className="h-3.5 w-3.5" />
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4">
        {CHIPS.map((chip) => {
          const n = counts[chip.countKey];
          // Hide empty filters (except All) so we don't show "0" chips.
          if (chip.key !== 'all' && n === 0) return null;
          const active = activeFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              aria-pressed={active}
              onClick={() => onFilterChange(chip.key)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                active
                  ? 'border-brand bg-brand text-brand-fg'
                  : 'border-border bg-bg-secondary text-fg',
              )}
            >
              {chip.icon}
              <span>{chip.label}</span>
              <span className={cn('font-bold', active ? 'text-brand-fg/80' : 'text-fg-muted')}>{n}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
